import React from 'react';
import { PostCard } from './PostCard';
import type { Post, VideoDisplayOptions } from '../types';
import { ChevronUp, ChevronDown, Keyboard } from 'lucide-react';

interface FeedViewProps {
  posts: Post[];
  loadNextPost: () => void;
  loadPreviousPost: () => void;
  hasMore: boolean;
  hasPrevious: boolean;
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  displayOptions: VideoDisplayOptions;
  onChatClick: (post: Post) => void;
  isDesktop?: boolean;
}

export function FeedView({
  posts,
  loadNextPost,
  loadPreviousPost,
  hasMore,
  hasPrevious,
  currentIndex,
  setCurrentIndex,
  displayOptions,
  onChatClick,
  isDesktop = false
}: FeedViewProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const [showKeyboardTip, setShowKeyboardTip] = React.useState(isDesktop);

  // Hide keyboard tip after a delay
  React.useEffect(() => {
    if (showKeyboardTip) {
      const timer = setTimeout(() => {
        setShowKeyboardTip(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showKeyboardTip]);

  const handleScroll = React.useCallback((direction: 1 | -1) => {
    if (isScrolling) return;
    
    setIsScrolling(true);
    
    // Load more content if needed
    if (direction > 0 && currentIndex === posts.length - 1 && hasMore) {
      loadNextPost();
    } else if (direction < 0 && currentIndex === 0 && hasPrevious) {
      loadPreviousPost();
    }

    const newIndex = Math.max(0, Math.min(posts.length - 1, currentIndex + direction));
    setCurrentIndex(newIndex);
    
    // Reset scrolling lock after animation
    setTimeout(() => {
      setIsScrolling(false);
    }, 300);
  }, [currentIndex, posts.length, hasMore, hasPrevious, loadNextPost, loadPreviousPost, setCurrentIndex, isScrolling]);

  // Handle wheel events
  React.useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (Math.abs(e.deltaY) > 0) {
        handleScroll(e.deltaY > 0 ? 1 : -1);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleScroll]);

  // Handle touch events
  React.useEffect(() => {
    let touchStart = 0;
    
    const handleTouchStart = (e: TouchEvent) => {
      touchStart = e.touches[0].clientY;
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      const touchEnd = e.changedTouches[0].clientY;
      const delta = touchStart - touchEnd;
      
      if (Math.abs(delta) > 50) { // Minimum swipe distance
        handleScroll(delta > 0 ? 1 : -1);
      }
    };

    // Only add touch handlers on mobile
    if (!isDesktop) {
      const container = containerRef.current;
      if (container) {
        container.addEventListener('touchstart', handleTouchStart);
        container.addEventListener('touchend', handleTouchEnd);
      }

      return () => {
        if (container) {
          container.removeEventListener('touchstart', handleTouchStart);
          container.removeEventListener('touchend', handleTouchEnd);
        }
      };
    }
  }, [handleScroll, isDesktop]);

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 overflow-hidden ${isDesktop ? 'pt-16 ml-[240px]' : 'pt-16 pb-14'}`}
    >
      {posts.map((post, index) => (
        <div 
          key={post.id}
          className="absolute inset-0 w-full h-full transition-transform duration-300"
          style={{
            transform: `translateY(${(index - currentIndex) * 100}%)`,
            zIndex: index === currentIndex ? 1 : 0
          }}
        >
          <PostCard
            post={post}
            isVisible={index === currentIndex}
            displayOptions={displayOptions}
            onChatClick={() => onChatClick(post)}
            isDesktop={isDesktop}
          />
        </div>
      ))}
      
      {isDesktop && (
        <>
          {/* Desktop navigation controls */}
          <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-20 flex flex-col gap-4 items-center">
            <button
              onClick={() => handleScroll(-1)}
              disabled={!hasPrevious && currentIndex === 0}
              className={`p-3 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 transition-colors ${
                !hasPrevious && currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label="Previous video"
            >
              <ChevronUp className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={() => handleScroll(1)}
              disabled={!hasMore && currentIndex === posts.length - 1}
              className={`p-3 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 transition-colors ${
                !hasMore && currentIndex === posts.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label="Next video"
            >
              <ChevronDown className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Desktop keyboard shortcuts tip */}
          {showKeyboardTip && (
            <div className="fixed left-1/2 bottom-8 transform -translate-x-1/2 z-20 bg-black/80 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3 text-white animate-fadeIn">
              <Keyboard className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-sm font-medium">Keyboard shortcuts available</p>
                <p className="text-xs text-gray-400">↑/↓ or J/K: Navigation • M: Toggle mute • F: Toggle fullscreen</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}